import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { queryKeys } from "../services/queryKeys";
import {
  fetchStoryShareLinks,
  resetShareLink,
  updateShareLink,
} from "../services/shareLinks/shareLinksApi";
import type {
  ShareLink,
  ShareLinkKind,
  StoryShareLinks,
  UpdateShareLinkRequest,
} from "../services/shareLinks/shareLinks.types";

/** Both share rows (link + embed) of a story, with put and reset mutations that update the cache. */
export const useStoryShareLinks = (storyId: string, enabled = true) => {
  const client = useQueryClient();
  const key = queryKeys.storyShareLinks.detail(storyId);
  const query = useQuery({
    queryKey: key,
    queryFn: () => fetchStoryShareLinks(storyId),
    enabled: enabled && Boolean(storyId),
  });
  const store = (kind: ShareLinkKind, row: ShareLink) =>
    client.setQueryData<StoryShareLinks>(key, (prev) => (prev ? { ...prev, [kind]: row } : prev));
  const put = useMutation({
    mutationFn: (v: { kind: ShareLinkKind; body: UpdateShareLinkRequest }) =>
      updateShareLink(storyId, v.kind, v.body),
    onSuccess: (row, v) => store(v.kind, row),
  });
  const reset = useMutation({
    mutationFn: (kind: ShareLinkKind) => resetShareLink(storyId, kind),
    onSuccess: (row, kind) => store(kind, row),
  });
  return { query, put, reset };
};
